import { displayContent } from 'modules/templateEditor/common';
import { Button, BUTTON_VARIANTS } from 'ui/button';
import { Folder } from 'ui/icons';
import { ErrorBanner } from 'ui/banner';
import { SmartOptIn } from 'ui/illustrations';
import { isEmpty } from '@iterable/utils';
import { BasicSelectFormField } from './components/BasicSelectFormField';
import { InputTextFormField } from './components/InputTextFormField';
import { useSmsSmartOptInContext } from './contexts/SmsSmartOptInContext';
import {
  ButtonsWrapper,
  FormBorder,
  FormWrapper,
  Header,
  HeroBanner,
  HeroBody,
  HeroTitle,
  OptionalFieldLabel,
} from './SmsSmartOptIn.styled';
import { formatMessageTypeOptions, formatSenderOptions } from './SmsSmartOptInModal.utils';
import { SmsSmartOptInPreview } from './SmsSmartOptInPreview';
import { SmsSmartOptInLink } from './types';

const {
  btnSmsSmartOptInFormCancel,
  btnSmsSmartOptInFormCreateLink,
  btnSmsSmartOptInFormHeroBanner,
  btnSmsSmartOptInFormSave,
  headerSmsSmartOptInFormAdd,
  headerSmsSmartOptInFormHeroBanner,
  labelSmsSmartOptInFormBrandName,
  labelSmsSmartOptInFormDescription,
  labelSmsSmartOptInFormMessageType,
  labelSmsSmartOptInFormName,
  labelSmsSmartOptInFormOptionalText,
  labelSmsSmartOptInFormPrivacyLink,
  labelSmsSmartOptInFormRedirectLink,
  labelSmsSmartOptInFormSender,
  labelSmsSmartOptInFormTermsLink,
  strSmsSmartOptInDocumentationLinkUrl,
  strSmsSmartOptInFormBrandNameHelpText,
  strSmsSmartOptInFormErrorBannerText,
  strSmsSmartOptInFormHeroBannerContent,
  strSmsSmartOptInFormMessageTypeHelpText,
  strSmsSmartOptInFormSenderHelpText,
} = displayContent;

export const SmsSmartOptInForm = () => {
  const {
    smsSenders,
    messageTypes: smsOptInMessageTypes,
    assetTypeName: typeName,
    assetDetails: smartLink,
    assetErrors: smartLinkErrors,
    updateAssetDetails: updateSmartLinkDetails,
    save,
    cancel,
    setChangesMade,
  } = useSmsSmartOptInContext();

  const messageTypeOptions = formatMessageTypeOptions(smsOptInMessageTypes);
  const senderOptions = formatSenderOptions(smsSenders);

  const getBasicSelectDefaultValue = (selectedOptionId?: number) => {
    return selectedOptionId?.toString() ? [selectedOptionId.toString()] : undefined;
  };

  const getLabelWithOptionalText = (label: string) => (
    <>
      {label}
      <OptionalFieldLabel>{labelSmsSmartOptInFormOptionalText}</OptionalFieldLabel>
    </>
  );

  const handleFieldChange = (fieldName: keyof SmsSmartOptInLink, newValue: string | undefined) => {
    const transformedValue =
      fieldName === 'smsSenderId' || fieldName === 'messageTypeId'
        ? Number.parseInt(newValue || '', 10)
        : newValue;
    updateSmartLinkDetails({
      ...smartLink,
      [fieldName]: transformedValue,
    });
    setChangesMade(true);
  };

  return (
    <FormWrapper data-test="sms-smart-opt-in-form">
      <HeroBanner Icon={null}>
        <div>
          <HeroTitle>{headerSmsSmartOptInFormHeroBanner}</HeroTitle>
          <HeroBody>
            <div>{strSmsSmartOptInFormHeroBannerContent}</div>
            <a href={strSmsSmartOptInDocumentationLinkUrl} target="_blank">
              <Button variant={BUTTON_VARIANTS.SECONDARY}>
                <Folder />
                <span>{btnSmsSmartOptInFormHeroBanner}</span>
              </Button>
            </a>
          </HeroBody>
        </div>
        <SmartOptIn width={136} height={136} />
      </HeroBanner>
      <FormBorder>
        <Header>
          <span>{`${headerSmsSmartOptInFormAdd} ${typeName}`}</span>
          <ButtonsWrapper>
            <Button
              data-test="sms-smart-opt-in-form-cancel-button"
              onClick={cancel}
              variant={BUTTON_VARIANTS.SECONDARY}
            >
              {btnSmsSmartOptInFormCancel}
            </Button>
            <Button data-test="sms-smart-opt-in-form-save-button" onClick={() => save()}>
              {smartLink.isNewAsset ? btnSmsSmartOptInFormCreateLink : btnSmsSmartOptInFormSave}
            </Button>
          </ButtonsWrapper>
        </Header>
        {!isEmpty(smartLinkErrors) && (
          <ErrorBanner>{`${strSmsSmartOptInFormErrorBannerText} ${typeName}.`}</ErrorBanner>
        )}
        <InputTextFormField
          id="sms-smart-opt-in-form-field-display-name"
          errorText={smartLinkErrors?.name}
          hasError={!!smartLinkErrors?.name}
          labelText={labelSmsSmartOptInFormName}
          onChange={(displayName) => handleFieldChange('name', displayName)}
          value={smartLink?.name || ''}
        />
        <InputTextFormField
          id="sms-smart-opt-in-form-field-description"
          labelText={getLabelWithOptionalText(labelSmsSmartOptInFormDescription)}
          onChange={(description) => handleFieldChange('description', description)}
          value={smartLink?.description || ''}
        />
        <InputTextFormField
          id="sms-smart-opt-in-form-field-brand-name"
          errorText={smartLinkErrors?.brandName}
          hasError={!!smartLinkErrors?.brandName}
          helpText={strSmsSmartOptInFormBrandNameHelpText}
          labelText={labelSmsSmartOptInFormBrandName}
          onChange={(brandName) => handleFieldChange('brandName', brandName)}
          value={smartLink?.brandName || ''}
          maxLength={50}
          restrictionText={`${smartLink?.brandName?.length || 0}/50 characters`}
        />
        <BasicSelectFormField
          id="sms-smart-opt-in-form-field-message-type"
          defaultValues={getBasicSelectDefaultValue(smartLink?.messageTypeId)}
          helpText={strSmsSmartOptInFormMessageTypeHelpText}
          labelText={labelSmsSmartOptInFormMessageType}
          errorText={smartLinkErrors?.messageTypeId}
          hasError={!!smartLinkErrors?.messageTypeId}
          options={messageTypeOptions}
          onSelect={([selected]) =>
            handleFieldChange(
              'messageTypeId',
              messageTypeOptions.find((option) => option.id === selected?.toString())?.id,
            )
          }
        />
        <BasicSelectFormField
          id="sms-smart-opt-in-form-field-sender"
          defaultValues={getBasicSelectDefaultValue(smartLink?.smsSenderId)}
          helpText={strSmsSmartOptInFormSenderHelpText}
          labelText={labelSmsSmartOptInFormSender}
          errorText={smartLinkErrors?.smsSenderId}
          hasError={!!smartLinkErrors?.smsSenderId}
          options={senderOptions}
          onSelect={([selected]) =>
            handleFieldChange(
              'smsSenderId',
              senderOptions.find((option) => option.id === selected?.toString())?.id,
            )
          }
        />
        <InputTextFormField
          errorText={smartLinkErrors?.termsLink}
          hasError={!!smartLinkErrors?.termsLink}
          id="sms-smart-opt-in-form-field-terms-link"
          labelText={getLabelWithOptionalText(labelSmsSmartOptInFormTermsLink)}
          onChange={(termsLink) => handleFieldChange('termsLink', termsLink)}
          value={smartLink?.termsLink || ''}
        />
        <InputTextFormField
          errorText={smartLinkErrors?.privacyLink}
          hasError={!!smartLinkErrors?.privacyLink}
          id="sms-smart-opt-in-form-field-privacy-link"
          labelText={getLabelWithOptionalText(labelSmsSmartOptInFormPrivacyLink)}
          onChange={(privacyLink) => handleFieldChange('privacyLink', privacyLink)}
          value={smartLink?.privacyLink || ''}
        />
        <InputTextFormField
          errorText={smartLinkErrors?.fallbackUrl}
          hasError={!!smartLinkErrors?.fallbackUrl}
          id="sms-smart-opt-in-form-field-redirect-link"
          labelText={labelSmsSmartOptInFormRedirectLink}
          onChange={(redirectLink) => handleFieldChange('fallbackUrl', redirectLink)}
          value={smartLink?.fallbackUrl || ''}
        />
        <SmsSmartOptInPreview />
      </FormBorder>
    </FormWrapper>
  );
};
